import React, { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { API } from "@/lib/api";

const SPORTS = [
  "Basketball", "Volleyball", "Football", "Soccer", "Baseball/Softball",
  "Track & Field", "Tennis", "Swimming", "Lacrosse", "Hockey", "Cheerleading",
  "Gymnastics", "Wrestling", "Cross Country", "Other"
];

const GRADES = [
  "5th", "6th", "7th", "8th", "9th", "10th", "11th", "12th", "college", "pro"
];

const METRIC_NAMES = {
  sj: "Squat Jump", vj: "Vertical Jump", bj: "Broad Jump", rsi: "RSI", ag: "Approach Gain"
};

const VALID_TOKEN = "paladin-maxsc-2026";

export default function JumpAssessment() {
  const { token } = useParams();
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [emailSent, setEmailSent] = useState(false);

  const [form, setForm] = useState({
    athlete_name: "", parent_name: "", parent_email: "", parent_phone: "",
    age_grade: "9th", sport: "Basketball", gender: "male",
    height_feet: "", height_inches: "",
    bodyweight: "",
    reach_feet: "", reach_inches: "",
    squat_jump: "", vertical_jump: "", broad_jump: "", rsi: "",
    approach_feet: "", approach_inches: "",
    iso_squat_force: "", rsi_jump_height: "", rsi_gct: "",
    broad_jump_feet: "", broad_jump_inches: "",
  });

  const [testsEnabled, setTestsEnabled] = useState({
    squat_jump: true, vertical_jump: true, broad_jump: true, rsi: true, approach_jump: true, iso_squat_force: false,
  });

  const [technique, setTechnique] = useState({
    vertical_torso: false, long_penultimate: false, large_arm_swing: false,
  });

  const toggleTechnique = (key) => {
    setTechnique(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleTest = (key) => {
    setTestsEnabled(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const numVal = (v) => parseFloat(v) || 0;

  // Live calculations
  const reachInches = (parseInt(form.reach_feet) || 0) * 12 + (parseFloat(form.reach_inches) || 0);
  const approachTouchInches = (parseInt(form.approach_feet) || 0) * 12 + (parseFloat(form.approach_inches) || 0);

  const approachGain = useMemo(() => {
    const touch = approachTouchInches;
    const reach = reachInches;
    const vj = numVal(form.vertical_jump);
    if (touch > 0 && reach > 0 && vj > 0) return ((touch - reach) / vj).toFixed(2);
    return "—";
  }, [approachTouchInches, reachInches, form.vertical_jump]);

  if (token !== VALID_TOKEN) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center p-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-2">Access Denied</h1>
          <p className="text-slate-400">This page requires a valid access link.</p>
        </div>
      </div>
    );
  }

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const canSubmit = form.athlete_name && form.parent_name && form.parent_email && form.bodyweight &&
    form.height_feet !== "" &&
    (!testsEnabled.squat_jump || form.squat_jump) &&
    (!testsEnabled.vertical_jump || form.vertical_jump) &&
    (!testsEnabled.broad_jump || form.broad_jump_feet) &&
    (!testsEnabled.rsi || form.rsi) &&
    (!testsEnabled.approach_jump || (form.approach_feet && form.reach_feet !== ""));

  const handleSubmit = async () => {
    if (!canSubmit) {
      toast.error("Please fill all required fields");
      return;
    }
    setSending(true);
    try {
      const payload = {
        ...form,
        bodyweight: numVal(form.bodyweight),
        height_inches: (parseInt(form.height_feet) || 0) * 12 + (parseInt(form.height_inches) || 0),
        reach_inches: reachInches || null,
        squat_jump: testsEnabled.squat_jump ? numVal(form.squat_jump) : null,
        vertical_jump: testsEnabled.vertical_jump ? numVal(form.vertical_jump) : null,
        broad_jump: testsEnabled.broad_jump ? ((parseInt(form.broad_jump_feet) || 0) * 12 + (parseInt(form.broad_jump_inches) || 0)) || null : null,
        rsi: testsEnabled.rsi ? numVal(form.rsi) : null,
        rsi_jump_height: testsEnabled.rsi ? numVal(form.rsi_jump_height) : null,
        rsi_gct: testsEnabled.rsi && form.rsi_gct ? numVal(form.rsi_gct) / 1000 : null,
        approach_jump: testsEnabled.approach_jump ? approachTouchInches : null,
        approach_touch_inches: testsEnabled.approach_jump ? approachTouchInches : null,
        iso_squat_force: testsEnabled.iso_squat_force ? numVal(form.iso_squat_force) : null,
        tests_enabled: testsEnabled,
        technique_notes: technique,
        access_token: token,
      };
      const res = await fetch(`${API}/jump-assessment/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Submission failed");
      const data = await res.json();
      setResult(data.assessment);
      setSubmitted(true);
      toast.success("Assessment calculated!");
    } catch (err) {
      toast.error(err.message || "Submission failed");
    } finally {
      setSending(false);
    }
  };

  const handleSendEmail = async () => {
    if (!result?.assessment_id) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/jump-assessment/send-report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ assessment_id: result.assessment_id, access_token: token }),
      });
      let data;
      try {
        data = await res.json();
      } catch {
        throw new Error("Server error — please try again");
      }
      if (!res.ok) {
        throw new Error(data.detail || "Send failed");
      }
      setEmailSent(true);
      toast.success(data.message || "Report sent!");
    } catch (err) {
      toast.error(err.message || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  const handleReset = () => {
    setForm({
      athlete_name: "", parent_name: "", parent_email: "", parent_phone: "",
      age_grade: "9th", sport: "Basketball", gender: "male",
      height_feet: "", height_inches: "",
      bodyweight: "",
      reach_feet: "", reach_inches: "",
      squat_jump: "", vertical_jump: "", rsi: "",
      approach_feet: "", approach_inches: "",
      iso_squat_force: "", rsi_jump_height: "", rsi_gct: "",
      broad_jump_feet: "", broad_jump_inches: "",
    });
    setTestsEnabled({
      squat_jump: true, vertical_jump: true, broad_jump: true, rsi: true, approach_jump: true, iso_squat_force: false,
    });
    setSubmitted(false);
    setResult(null);
    setEmailSent(false);
  };

  const getDomColor = (val) => {
    if (val >= 100) return "text-green-400";
    if (val >= 75) return "text-amber-400";
    return "text-red-400";
  };

  return (
    <div className="min-h-screen bg-[#0f172a] text-slate-100">
      <Toaster position="top-center" theme="dark" richColors />

      {/* Header */}
      <header className="bg-[#1e293b] border-b border-slate-700/50 px-4 py-4" data-testid="assessment-header">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold tracking-wide">JUMP ASSESSMENT</h1>
            <p className="text-slate-400 text-xs sm:text-sm mt-0.5">MaxSC Performance x Paladin</p>
          </div>
          {submitted && (
            <button
              onClick={handleReset}
              className="text-sm bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg transition-colors"
              data-testid="new-assessment-btn"
            >
              New Assessment
            </button>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 sm:p-6 space-y-6">

        {/* ==================== FORM ==================== */}
        {!submitted && (
          <>
            {/* Section 1: Athlete Info */}
            <section className="bg-[#1e293b] rounded-xl p-4 sm:p-5 space-y-4" data-testid="athlete-info-section">
              <h2 className="text-base font-semibold text-white border-b border-slate-600/50 pb-2">Athlete Information</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <InputField label="Athlete Name *" value={form.athlete_name} onChange={v => updateField("athlete_name", v)} testId="athlete-name" />
                <InputField label="Parent Name *" value={form.parent_name} onChange={v => updateField("parent_name", v)} testId="parent-name" />
                <InputField label="Parent Email *" type="email" value={form.parent_email} onChange={v => updateField("parent_email", v)} testId="parent-email" />
                <InputField label="Parent Phone" type="tel" value={form.parent_phone} onChange={v => updateField("parent_phone", v)} testId="parent-phone" />
                <SelectField label="Gender *" value={form.gender} onChange={v => updateField("gender", v)} options={[
                  { value: "male", label: "Male" }, { value: "female", label: "Female" }
                ]} testId="gender-select" />
                <SelectField label="Grade *" value={form.age_grade} onChange={v => updateField("age_grade", v)}
                  options={GRADES.map(g => ({ value: g, label: g.charAt(0).toUpperCase() + g.slice(1) + (["5th","6th","7th","8th","9th","10th","11th","12th"].includes(g) ? " Grade" : "") }))}
                  testId="grade-select"
                />
                <SelectField label="Sport *" value={form.sport} onChange={v => updateField("sport", v)}
                  options={SPORTS.map(s => ({ value: s, label: s }))} testId="sport-select" />
                {/* Height in feet + inches */}
                <div>
                  <label className="block text-xs text-slate-400 mb-1 font-medium">Height *</label>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <select value={form.height_feet} onChange={e => updateField("height_feet", e.target.value)}
                        className="w-full bg-[#0f172a] border border-[#334155] rounded-lg px-3 py-2.5 text-white text-sm focus:border-blue-500 outline-none"
                        data-testid="height-feet">
                        <option value="">Ft</option>
                        {[3,4,5,6,7].map(f => <option key={f} value={f}>{f} ft</option>)}
                      </select>
                    </div>
                    <div className="flex-1">
                      <select value={form.height_inches} onChange={e => updateField("height_inches", e.target.value)}
                        className="w-full bg-[#0f172a] border border-[#334155] rounded-lg px-3 py-2.5 text-white text-sm focus:border-blue-500 outline-none"
                        data-testid="height-inches">
                        <option value="">In</option>
                        {Array.from({length: 12}, (_, i) => <option key={i} value={i}>{i} in</option>)}
                      </select>
                    </div>
                  </div>
                </div>
                {/* Bodyweight */}
                <NumericField label="Bodyweight (lbs) *" value={form.bodyweight} onChange={v => updateField("bodyweight", v)} testId="bodyweight" />
                {/* Reach in feet + inches (with .5" increments) */}
                <div>
                  <label className="block text-xs text-slate-400 mb-1 font-medium">Reach</label>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <select value={form.reach_feet} onChange={e => updateField("reach_feet", e.target.value)}
                        className="w-full bg-[#0f172a] border border-[#334155] rounded-lg px-3 py-2.5 text-white text-sm focus:border-blue-500 outline-none"
                        data-testid="reach-feet">
                        <option value="">Ft</option>
                        {[6,7,8,9,10].map(f => <option key={f} value={f}>{f} ft</option>)}
                      </select>
                    </div>
                    <div className="flex-1">
                      <select value={form.reach_inches} onChange={e => updateField("reach_inches", e.target.value)}
                        className="w-full bg-[#0f172a] border border-[#334155] rounded-lg px-3 py-2.5 text-white text-sm focus:border-blue-500 outline-none"
                        data-testid="reach-inches">
                        <option value="">In</option>
                        {Array.from({length: 24}, (_, i) => {
                          const val = i * 0.5;
                          return <option key={val} value={val}>{val % 1 === 0 ? `${val}` : `${val}`} in</option>;
                        })}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 2: Test Inputs */}
            <section className="bg-[#1e293b] rounded-xl p-4 sm:p-5 space-y-4" data-testid="test-inputs-section">
              <h2 className="text-base font-semibold text-white border-b border-slate-600/50 pb-2">Test Results</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Toggleable tests */}
                <ToggleNumericField label="Squat Jump (in)" checked={testsEnabled.squat_jump} onToggle={() => toggleTest("squat_jump")}
                  value={form.squat_jump} onChange={v => updateField("squat_jump", v)} testId="squat-jump" />
                <ToggleNumericField label="Vertical Jump (in)" checked={testsEnabled.vertical_jump} onToggle={() => toggleTest("vertical_jump")}
                  value={form.vertical_jump} onChange={v => updateField("vertical_jump", v)} testId="vertical-jump" />
                <div className={`${!testsEnabled.broad_jump ? "opacity-40" : ""} transition-opacity`}>
                  <label className="flex items-center gap-2 text-xs text-slate-400 mb-1 cursor-pointer select-none" data-testid="toggle-broad-jump">
                    <input type="checkbox" checked={testsEnabled.broad_jump} onChange={() => toggleTest("broad_jump")}
                      className="w-4 h-4 rounded border-slate-500 bg-[#0f172a] text-blue-500 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer" />
                    Broad Jump
                  </label>
                  <div className="flex gap-2">
                    <select value={form.broad_jump_feet} onChange={e => updateField("broad_jump_feet", e.target.value)}
                      disabled={!testsEnabled.broad_jump} data-testid="broad-jump-feet"
                      className="flex-1 bg-[#0f172a] border border-slate-600/50 rounded-lg px-3 py-2.5 text-white text-sm focus:border-blue-500 outline-none disabled:opacity-50 disabled:cursor-not-allowed">
                      <option value="">Ft</option>
                      {[3, 4, 5, 6, 7, 8, 9, 10].map(f => <option key={f} value={f}>{f} ft</option>)}
                    </select>
                    <select value={form.broad_jump_inches} onChange={e => updateField("broad_jump_inches", e.target.value)}
                      disabled={!testsEnabled.broad_jump} data-testid="broad-jump-inches"
                      className="flex-1 bg-[#0f172a] border border-slate-600/50 rounded-lg px-3 py-2.5 text-white text-sm focus:border-blue-500 outline-none disabled:opacity-50 disabled:cursor-not-allowed">
                      <option value="">In</option>
                      {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(i => <option key={i} value={i}>{i} in</option>)}
                    </select>
                  </div>
                </div>
                <ToggleNumericField label="RSI" checked={testsEnabled.rsi} onToggle={() => toggleTest("rsi")}
                  value={form.rsi} onChange={v => updateField("rsi", v)} step="0.01" testId="rsi" />
                <div className={`${!testsEnabled.approach_jump ? "opacity-40" : ""} transition-opacity`}>
                  <label className="flex items-center gap-2 text-xs text-slate-400 mb-1 cursor-pointer select-none" data-testid="toggle-approach-jump">
                    <input type="checkbox" checked={testsEnabled.approach_jump} onChange={() => toggleTest("approach_jump")}
                      className="w-4 h-4 rounded border-slate-500 bg-[#0f172a] text-blue-500 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer" />
                    Approach Jump (touched)
                  </label>
                  <div className="flex gap-2">
                    <select value={form.approach_feet} onChange={e => updateField("approach_feet", e.target.value)}
                      disabled={!testsEnabled.approach_jump} data-testid="approach-feet"
                      className="flex-1 bg-[#0f172a] border border-slate-600/50 rounded-lg px-3 py-2.5 text-white text-sm focus:border-blue-500 outline-none disabled:opacity-50 disabled:cursor-not-allowed">
                      <option value="">Ft</option>
                      {[6, 7, 8, 9, 10, 11, 12, 13].map(f => <option key={f} value={f}>{f} ft</option>)}
                    </select>
                    <select value={form.approach_inches} onChange={e => updateField("approach_inches", e.target.value)}
                      disabled={!testsEnabled.approach_jump} data-testid="approach-inches"
                      className="flex-1 bg-[#0f172a] border border-slate-600/50 rounded-lg px-3 py-2.5 text-white text-sm focus:border-blue-500 outline-none disabled:opacity-50 disabled:cursor-not-allowed">
                      <option value="">In</option>
                      {Array.from({length: 24}, (_, i) => {
                        const val = i * 0.5;
                        return <option key={val} value={val}>{val % 1 === 0 ? `${val}` : `${val}`} in</option>;
                      })}
                    </select>
                  </div>
                </div>
                <ToggleNumericField label="Iso Squat Force" checked={testsEnabled.iso_squat_force} onToggle={() => toggleTest("iso_squat_force")}
                  value={form.iso_squat_force} onChange={v => updateField("iso_squat_force", v)} testId="iso-squat" />
              </div>

              {/* Approach Jump Technique Notes */}
              {testsEnabled.approach_jump && (
                <div className="bg-[#0f172a] border border-[#334155] rounded-xl p-4 space-y-3" data-testid="technique-section">
                  <h3 className="text-sm font-semibold text-slate-300">Approach Jump Technique</h3>
                  <div className="space-y-2">
                    {[
                      { key: "vertical_torso", label: "Torso",
                        note: "Getting to a more vertical spine can add 1-2in on jump" },
                      { key: "long_penultimate", label: "Penultimate Step",
                        note: "Increased Penultimate speed and eccentric RFD will allow for more elastic transfer into jump" },
                      { key: "large_arm_swing", label: "Arm Swing",
                        note: "Added Shoulder mobility will help contribute to the jump height. Arms contribute 20%" },
                    ].map(({ key, label, note }) => (
                      <div key={key}>
                        <label className="flex items-center gap-3 cursor-pointer group">
                          <input
                            type="checkbox"
                            checked={technique[key]}
                            onChange={() => toggleTechnique(key)}
                            className="w-4 h-4 rounded border-slate-500 bg-[#1e293b] text-blue-500 focus:ring-blue-500/30"
                            data-testid={`technique-${key}`}
                          />
                          <span className="text-sm text-white group-hover:text-blue-300 transition-colors">{label}</span>
                        </label>
                        {technique[key] && (
                          <p className="ml-7 mt-1 text-xs text-amber-300/90 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2"
                             data-testid={`technique-note-${key}`}>
                            {note}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>

            {/* Submit */}
            <button
              onClick={handleSubmit}
              disabled={!canSubmit || sending}
              className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:text-slate-500 text-white py-3.5 rounded-xl font-semibold text-base transition-colors"
              data-testid="calculate-btn"
            >
              {sending ? "Calculating..." : "Calculate Assessment"}
            </button>
          </>
        )}

        {/* ==================== RESULTS ==================== */}
        {submitted && result && (
          <>
            {/* Athlete header */}
            <div className="bg-[#1e293b] rounded-xl p-4 sm:p-5 text-center" data-testid="result-header">
              <h2 className="text-xl font-bold">{result.athlete_name}</h2>
              <p className="text-slate-400 text-sm mt-1">
                {result.age_grade.charAt(0).toUpperCase() + result.age_grade.slice(1)} Grade
                {["5th","6th","7th","8th","9th","10th","11th","12th"].includes(result.age_grade) ? "" : ""} &middot; {result.sport} &middot; {result.gender === "male" ? "Male" : "Female"}
              </p>
            </div>

            {/* Main Scores */}
            <div className="grid grid-cols-2 gap-3" data-testid="main-scores">
              <ScoreCard label="Stage Score" sublabel="vs Age Group Avg" value={`${result.stage_score}%`} color="text-green-400" />
              <ScoreCard label="Elite Score" sublabel="vs Elite Level" value={`${result.elite_score}%`} color="text-blue-400" />
            </div>

            {/* Domination Levels */}
            <section className="bg-[#1e293b] rounded-xl p-4 sm:p-5" data-testid="domination-scores">
              <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-3">Domination Levels</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  ["middle_school", "Middle School"],
                  ["high_school", "High School"],
                  ["college", "College"],
                  ["pro", "Pro"],
                ].map(([key, label]) => (
                  <div key={key} className="bg-[#0f172a] rounded-lg p-3 text-center">
                    <p className="text-xs text-slate-400">{label}</p>
                    <p className={`text-xl font-bold mt-1 ${getDomColor(result.domination?.[key] || 0)}`} data-testid={`dom-${key}`}>
                      {result.domination?.[key] || 0}%
                    </p>
                  </div>
                ))}
              </div>
            </section>

            {/* Metrics Breakdown */}
            <section className="bg-[#1e293b] rounded-xl p-4 sm:p-5 overflow-x-auto" data-testid="metrics-breakdown">
              <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-3">Performance Breakdown</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-slate-400 text-xs">
                    <th className="text-left py-2 px-2">Metric</th>
                    <th className="text-center py-2 px-2">Score</th>
                    <th className="text-center py-2 px-2">Avg</th>
                    <th className="text-center py-2 px-2">% Avg</th>
                    <th className="text-center py-2 px-2">Elite</th>
                    <th className="text-center py-2 px-2">% Elite</th>
                  </tr>
                </thead>
                <tbody>
                  {(result.tested_metrics || ["sj", "vj", "rsi", "ag"]).map(key => {
                    const m = result.metrics?.[key] || {};
                    const isLimiter = key === result.primary_limiter;
                    return (
                      <tr key={key} className={isLimiter ? "bg-red-500/10" : ""}>
                        <td className="py-2 px-2 font-medium">
                          {METRIC_NAMES[key]}
                          {isLimiter && <span className="ml-1 text-red-400 text-xs">(Limiter)</span>}
                        </td>
                        <td className="text-center py-2 px-2 font-semibold">{m.value ?? "—"}</td>
                        <td className="text-center py-2 px-2 text-slate-400">{m.avg ?? "—"}</td>
                        <td className="text-center py-2 px-2">{m.pct_avg ?? "—"}%</td>
                        <td className="text-center py-2 px-2 text-slate-400">{m.elite ?? "—"}</td>
                        <td className="text-center py-2 px-2">{m.pct_elite ?? "—"}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </section>

            {/* Strengths & Limiter */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4" data-testid="strengths-card">
                <p className="text-xs font-semibold text-green-400 uppercase tracking-wider">Strengths</p>
                <p className="text-sm text-green-200 mt-1">
                  {(result.strengths || []).map(s => METRIC_NAMES[s] || s).join(", ")}
                </p>
              </div>
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4" data-testid="limiter-card">
                <p className="text-xs font-semibold text-red-400 uppercase tracking-wider">Primary Limiter</p>
                <p className="text-sm text-red-200 mt-1">{METRIC_NAMES[result.primary_limiter] || result.primary_limiter}</p>
              </div>
            </div>

            {/* Analysis */}
            <section className="bg-[#1e293b] rounded-xl p-4 sm:p-5 border-l-4 border-blue-500" data-testid="analysis-section">
              <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-2">Analysis</h3>
              <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-line">{result.analysis}</p>
            </section>

            {/* Send Report */}
            {!emailSent ? (
              <button
                onClick={handleSendEmail}
                disabled={sending}
                className="w-full bg-green-600 hover:bg-green-500 disabled:bg-slate-700 text-white py-3.5 rounded-xl font-semibold text-base transition-colors"
                data-testid="send-report-btn"
              >
                {sending ? "Sending..." : `Send Report to ${result.parent_email}`}
              </button>
            ) : (
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 text-center" data-testid="email-sent-confirm">
                <p className="text-green-400 font-semibold">Report Sent Successfully!</p>
                <p className="text-slate-400 text-sm mt-1">Sent to {result.parent_email}</p>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

// ==================== FORM COMPONENTS ====================

function InputField({ label, type = "text", value, onChange, testId }) {
  return (
    <div>
      <label className="block text-xs text-slate-400 mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full bg-[#0f172a] border border-slate-600/50 rounded-lg px-3 py-2.5 text-white text-sm focus:border-blue-500 outline-none"
        data-testid={testId}
      />
    </div>
  );
}

function NumericField({ label, value, onChange, step = "0.1", testId }) {
  return (
    <div>
      <label className="block text-xs text-slate-400 mb-1">{label}</label>
      <input
        type="number"
        inputMode="decimal"
        step={step}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full bg-[#0f172a] border border-slate-600/50 rounded-lg px-3 py-2.5 text-white text-sm focus:border-blue-500 outline-none"
        data-testid={testId}
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="block text-xs text-slate-400 mb-1">{label}</label>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full bg-[#0f172a] border border-slate-600/50 rounded-lg px-3 py-2.5 text-white text-sm focus:border-blue-500 outline-none"
        data-testid={testId}
      >
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );
}

function ToggleNumericField({ label, checked, onToggle, value, onChange, step = "0.1", testId }) {
  return (
    <div className={`${!checked ? "opacity-40" : ""} transition-opacity`}>
      <label className="flex items-center gap-2 text-xs text-slate-400 mb-1 cursor-pointer select-none" data-testid={`toggle-${testId}`}>
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          className="w-4 h-4 rounded border-slate-500 bg-[#0f172a] text-blue-500 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer"
        />
        {label}
      </label>
      <input
        type="number"
        inputMode="decimal"
        step={step}
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={!checked}
        className="w-full bg-[#0f172a] border border-slate-600/50 rounded-lg px-3 py-2.5 text-white text-sm focus:border-blue-500 outline-none disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid={testId}
      />
    </div>
  );
}

function ScoreCard({ label, sublabel, value, color }) {
  return (
    <div className="bg-[#1e293b] rounded-xl p-4 text-center">
      <p className="text-xs text-slate-400 uppercase tracking-wider">{label}</p>
      <p className={`text-3xl font-bold mt-1 ${color}`}>{value}</p>
      <p className="text-[10px] text-slate-500 mt-1">{sublabel}</p>
    </div>
  );
}
